import { inject, Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";

import { firstValueFrom } from "rxjs";

import { ConfirmDialogComponent } from "@components/shared";
import { IconConstants, TextConstants } from "@shared/constants";
import { CommonErrorEnum, ConfirmDialogResponseEnum } from "@shared/enums";

import { NotificationService } from "../notification.service";
import { RecordSharingService } from "./record-sharing.service";
import { BackendError, NotificationError } from "../notification.model";

@Injectable({
  providedIn: "root",
})
export class DeleteErrorHandlerService {
  private readonly dialog = inject(MatDialog);

  private readonly recordSharingService = inject(RecordSharingService);

  private readonly notificationService = inject(NotificationService);

  async handle(error: NotificationError, deleteFn: () => Promise<void>): Promise<void> {
    const errors = error?.error?.errors;

    if (!Array.isArray(errors)) {
      this.notificationService.showError(error);

      return;
    }

    const mappedErrors = errors.filter(
      ({ code }) => code === CommonErrorEnum.NOT_ALLOWED_TO_DELETE_MAPPED_RECORD,
    );

    if (mappedErrors.length) {
      await this.handleMappedRecordDelete(mappedErrors, deleteFn);

      return;
    }

    this.notificationService.showError(error);
  }

  private async handleMappedRecordDelete(
    mappedErrors: BackendError[],
    deleteFn: () => Promise<void>,
  ): Promise<void> {
    const mappedEntities = mappedErrors.map((e) => e.arguments);

    const htmlList = mappedEntities.map((e) => `<li>${e["other_entity_name"]}</li>`).join("");
    const contentText = $localize`:@@error-handler.dialogContent:The record you are trying to delete is mapped to one or more records that were shared with you. Deleting it will remove the mappings.`;
    const mappedLabel = $localize`:@@error-handler.mappedRecordsLabel:Mapped record(s):`;
    const contentHTML = `
      <p>${contentText}</p>
      <p><strong>${mappedLabel}</strong></p>
      <ul>${htmlList}</ul>
    `;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: $localize`:@@error-handler.recordhasmapping:Record has mapping`,
        contentHTML,
        confirmButtonText: $localize`:@@error-handler.removemappinganddelete:Remove mapping(s) & Delete`,
        confirmButtonColor: "danger",
        confirmButtonIcon: IconConstants.DELETE,
        cancelButtonText: TextConstants.CANCEL,
      },
    });

    const result = await firstValueFrom(dialogRef.afterClosed());

    if (result === ConfirmDialogResponseEnum.CONFIRM) {
      try {
        const allMappings = await this.recordSharingService.getAllMappings();

        const mappingIds = mappedEntities
          .map(
            (e) =>
              allMappings.find(
                (m) => m.localUri?.endsWith(e["id"]) && m.inboundUri?.endsWith(e["other_id"]),
              )?.id,
          )
          .filter(Boolean);

        if (!mappingIds.length) {
          this.notificationService.showError(
            $localize`:@@error-handler.notmappingtodelete:Could not identify any record mappings to remove.`,
          );

          return;
        }

        for (const mappingId of mappingIds) {
          await this.recordSharingService.removeInboundMapping(mappingId);
        }

        await deleteFn();
        this.notificationService.showSuccess(TextConstants.RECORD_DELETED);
      } catch (e) {
        this.notificationService.showError(e);
      }
    }
  }
}
